import { Injectable, inject } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { MatIconRegistry } from '@angular/material/icon';
import { AppIconCatalog } from './app-icon.types';

/** Gestiona los catálogos propios y registra cada SVG únicamente al utilizarlo. */
@Injectable({ providedIn: 'root' })
export class AppIconRegistry {
  private readonly matIconRegistry = inject(MatIconRegistry);
  private readonly sanitizer = inject(DomSanitizer);

  private readonly icons = new Map<string, string>();
  private readonly registeredIcons = new Set<string>();

  /**
   * Incorpora un catálogo al registro.
   *
   * Un nombre repetido solo es válido cuando conserva exactamente la misma URL.
   */
  addCatalog(catalog: AppIconCatalog): void {
    for (const [name, url] of Object.entries(catalog)) {
      const currentUrl = this.icons.get(name);

      if (currentUrl !== undefined && currentUrl !== url) {
        throw new Error(
          `The app icon "${name}" is already associated with "${currentUrl}" and cannot be associated with "${url}".`,
        );
      }

      this.icons.set(name, url);
    }
  }

  /** Indica si un nombre pertenece a alguno de los catálogos propios. */
  isCustomIcon(name: string): boolean {
    return this.icons.has(name);
  }

  /**
   * Registra el SVG en Angular Material cuando todavía no estaba registrado.
   *
   * @returns `true` si el nombre corresponde a un icono propio.
   */
  ensureRegistered(name: string): boolean {
    const url = this.icons.get(name);

    if (url === undefined) {
      return false;
    }

    if (!this.registeredIcons.has(name)) {
      // La URL es segura porque solo puede proceder de un catálogo controlado por el código.
      const trustedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
      this.matIconRegistry.addSvgIcon(name, trustedUrl);
      this.registeredIcons.add(name);
    }

    return true;
  }
}
